import { styled, css } from 'styled-components';

import { Media } from '../../../../../theme/Media.tsx';
import type { TableCellProps } from './types.ts';
import { TableCellContentType } from './types.ts';

export const TableCellWrapper = styled.td<Omit<TableCellProps, 'title'>>`
  overflow: hidden;
  padding: 16px 12px;
  vertical-align: middle;
  ${({ align }) =>
    align &&
    css`
      text-align: ${align};
    `}
  ${({ width }) =>
    width &&
    css`
      width: ${width}px;
    `}
  ${({ textBold, theme }) => textBold && theme.typography.weightMedium}
  ${({ breakWord }) =>
    breakWord &&
    css`
      word-break: break-all;
    `}
  ${({ noWrap }) =>
    noWrap &&
    css`
      white-space: nowrap;
    `}

  ${({ contentType }) =>
    contentType === TableCellContentType.SPACER &&
    css`
      && {
        height: 8px;
        min-height: 8px;
        background: transparent;
        padding: 0;
      }
    `}

  @media ${Media.smMin} {
    display: table-cell;
    height: 72px;
    min-height: 72px;

    ${({ contentType }) =>
      contentType === TableCellContentType.CHECKBOX &&
      css`
        padding: 0;
        width: 1px;
        height: 1px;
        @-moz-document url-prefix() {
          height: 100%;
        }
        position: sticky;
        left: 0;
        z-index: 2;
        border-right: 1px solid ${({ theme }) => theme.colors.grey10_1};

        & > * {
          width: 44px;
          background: ${({ theme }) => theme.colors.grey00_1};
          padding: 16px 12px;
          height: 100%;
        }
      `}

    ${({ contentType }) =>
      contentType === TableCellContentType.ACTION &&
      css`
        padding: 0;
        width: 1px;
        height: 1px;
        @-moz-document url-prefix() {
          height: 100%;
        }
        position: sticky;
        right: 0;
        z-index: 2;
        border-left: 1px solid ${({ theme }) => theme.colors.grey10_1};

        & > * {
          width: 60px;
          background: ${({ theme }) => theme.colors.grey00_1};
          padding: 16px 12px;
          height: 100%;
        }
      `}
      ${({ withRightBorder, theme }) =>
      withRightBorder &&
      css`
        border-right: 1px solid ${theme.colors.grey10_1};
      `}

      ${({ contentType }) =>
      contentType === TableCellContentType.NO_PADDING &&
      css`
        padding: 0;
        width: 1px;
        height: 1px;
        width: 100%;
        @-moz-document url-prefix() {
          height: 100%;
        }
        & > div > div {
          width: 100%;
        }
      `}

      ${({ contentType }) =>
      contentType === TableCellContentType.COMPACT &&
      css`
        padding: 8px 16px;
        min-height: 20px;
        height: auto;
      `}

    ${({ contentType }) =>
      contentType === TableCellContentType.GROUP_HEADER &&
      css`
        height: unset;
        padding: unset;
      `}
  }

  @media ${Media.xsMax} {
    display: block;
    min-height: 36px;
    height: unset;
    padding: 8px 12px;
    width: 100%;
    word-break: break-all;

    & + & {
      border-top: 1px solid ${({ theme }) => theme.colors.grey10_1};
    }

    ${({ contentType }) =>
      contentType === TableCellContentType.GROUP_HEADER &&
      css`
        border-bottom: none;
        border-top-left-radius: 8px;
        border-top-right-radius: 8px;
        ${({ theme }) => theme.typography.textMedium}
        color: ${({ theme }) => theme.colors.grey100_1};
        padding: 0;
        min-height: unset;
      `}
  }
`;

export const TableCellInnerWrapper = styled.div<Omit<TableCellProps, 'title'>>`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: ${({ noWrap }) => (noWrap ? 'nowrap' : 'wrap')};
  min-width: ${({ width }) => width && `${width}px`};

  @media ${Media.smMin} {
    justify-content: ${({ align }) => align};

    ${({ contentType }) =>
      (contentType === TableCellContentType.CHECKBOX || contentType === TableCellContentType.ACTION) &&
      css`
        justify-content: center;
      `}
  }

  @media ${Media.xsMax} {
    justify-content: space-between;

    ${({ contentType }) =>
      contentType === TableCellContentType.ACTION &&
      css`
        justify-content: flex-end;
      `}
  }
`;

export const TableCellInnerTitle = styled.div`
  ${({ theme }) => theme.typography.weightDefault}

  @media ${Media.smMin} {
    display: none;
  }
`;

interface TableCellInnerContentProps {
  $contentType?: TableCellContentType;
}

export const TableCellInnerContent = styled.div<TableCellInnerContentProps>`
  display: flex;
  flex-direction: column;
  ${({ $contentType }) =>
    $contentType === TableCellContentType.LOADING &&
    css`
      justify-content: center;
      width: 100%;
    `}

  @media ${Media.xsMax} {
    gap: 4px;
    justify-content: center;
    text-align: right;
  }
`;
